import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useReducedMotion } from 'framer-motion';
import { ReelCard } from '../data/ads';
import { track, EVENTS } from '../utils/analytics';

export const ReelVideo: React.FC<{ card: ReelCard }> = ({ card }) => {
  const ref = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const visibleRef = useRef(false);
  const reduced = useReducedMotion();
  const [playing, setPlaying] = useState(false);
  const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
  const shouldAutoPlay = !reduced && !saveData;

  const loadAndPlay = useCallback(() => {
    const video = videoRef.current;
    if (!video || !card.src) return;
    if (!video.src) { video.src = card.src; video.load(); }
    video.play().catch(() => setPlaying(false));
  }, [card.src]);

  useEffect(() => {
    if (!ref.current) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const visible = entry.isIntersecting && entry.intersectionRatio >= 0.05;
          visibleRef.current = visible;
          if (visible) {
            const video = videoRef.current;
            if (video && card.src && !video.src) { video.src = card.src; video.load(); }
            if (video) video.autoplay = shouldAutoPlay;
            if (shouldAutoPlay && video?.readyState && video.readyState >= 2) loadAndPlay();
          } else {
            videoRef.current?.pause();
            if (videoRef.current) videoRef.current.autoplay = false;
          }
        });
      },
      { threshold: [0, 0.05] }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [card.src, loadAndPlay, shouldAutoPlay]);

  return (
    <div className="reel-card__media" ref={ref}>
      <video
        ref={videoRef}
        poster={card.poster}
        muted
        loop
        playsInline
        autoPlay={shouldAutoPlay}
        preload="none"
        aria-label={card.alt}
        onClick={() => {
          const video = videoRef.current;
          if (video && !video.paused) video.pause();
          else loadAndPlay();
        }}
        onCanPlay={() => { if (shouldAutoPlay && visibleRef.current) loadAndPlay(); }}
        onPlaying={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      {!playing && <button className="reel-card__play" type="button" onClick={loadAndPlay} aria-label={`Play ${card.alt || 'video'}`}><span aria-hidden="true">▶</span> Play</button>}
    </div>
  );
};

const ReelStill: React.FC<{ card: ReelCard }> = ({ card }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return undefined;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => setVisible(e.isIntersecting)),
      { threshold: 0.2 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <div className={`reel-card__media${visible ? ' is-visible' : ''}`} ref={ref}>
      <img src={card.poster} alt={card.alt || ''} loading="lazy" decoding="async" />
    </div>
  );
};

const EndCard: React.FC<{ card: ReelCard }> = ({ card }) => (
  <Link to={card.href || '/contact'} className="reel-card reel-card--end" onClick={() => track(EVENTS.ctaClicked, { location: 'reel', label: 'Get a free audit' })}>
    <span className="reel-card__endtext">
      Next spot: yours.
      <br />
      Get a free audit →
    </span>
  </Link>
);

interface ReelProps {
  cards: readonly ReelCard[];
}

export interface ReelHandle {
  scroll: (dir: 1 | -1) => void;
}

/**
 * The ad reel: a horizontal snap row of short video spots, with an optional
 * campaign frame. Native scroll-snap everywhere; a light
 * pointer-drag on desktop; arrow buttons (rendered by the caller, next to the
 * section head) scroll by one card width via the exposed ref. Shared between
 * Home and Marketing so the same frames never render twice.
 */
const Reel = forwardRef<ReelHandle, ReelProps>(({ cards }, ref) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ down: boolean; startX: number; startScroll: number }>({
    down: false,
    startX: 0,
    startScroll: 0,
  });

  const scrollByCard = useCallback((dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('.reel-card');
    const step = (card?.offsetWidth || 260) + 12;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  }, []);

  useImperativeHandle(ref, () => ({ scroll: scrollByCard }), [scrollByCard]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'touch' || !trackRef.current) return;
    if ((e.target as HTMLElement).closest('button, a, video')) return;
    dragState.current = { down: true, startX: e.clientX, startScroll: trackRef.current.scrollLeft };
    trackRef.current.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragState.current.down || !trackRef.current) return;
    trackRef.current.scrollLeft = dragState.current.startScroll - (e.clientX - dragState.current.startX);
  };
  const endDrag = () => {
    dragState.current.down = false;
  };

  return (
    <div className="reel2">
      <div
        className="reel2__track"
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
      >
        {cards.map((card) =>
          card.kind === 'end' ? (
            <EndCard card={card} key={card.id} />
          ) : (
            <figure
              className={`reel-card${card.layout && card.layout !== 'phone' ? ` reel-card--${card.layout}` : ''}`}
              key={card.id}
            >
              {card.href ? (
                <a href={card.href} target="_blank" rel="noopener noreferrer" className="reel-card__link">
                  {card.kind === 'video' ? <ReelVideo card={card} /> : <ReelStill card={card} />}
                </a>
              ) : card.kind === 'video' ? (
                <ReelVideo card={card} />
              ) : (
                <ReelStill card={card} />
              )}
              {card.caption && <figcaption className="reel-card__cap">{card.caption}</figcaption>}
            </figure>
          )
        )}
      </div>
    </div>
  );
});

export default React.memo(Reel);
export const ReelArrows: React.FC<{ target: React.RefObject<ReelHandle> }> = ({ target }) => (
  <div className="reel2__arrows">
    <button type="button" aria-label="Scroll left" onClick={() => target.current?.scroll(-1)}>
      <ChevronLeft width={18} height={18} aria-hidden="true" />
    </button>
    <button type="button" aria-label="Scroll right" onClick={() => target.current?.scroll(1)}>
      <ChevronRight width={18} height={18} aria-hidden="true" />
    </button>
  </div>
);
